import AudioPlayer from "audio_player"
import Decimal from "decimal.js-light"
import Waveform from "waveform"
import { hhMMSS } from "utils/time"

export default class Playlist {
  constructor() {
    this.boundHandleTrackSelection = this.handleTrackSelection.bind(this)
    this.boundHandlePlayBtnClick = this.handlePlayBtnClick.bind(this)
    this.boundHandlePrevBtnClick = this.handlePrevBtnClick.bind(this)
    this.boundHandleNextBtnClick = this.handleNextBtnClick.bind(this)
    this.boundHandleZoomInBtnClick = this.handleZoomInBtnClick.bind(this)
    this.boundHandleZoomOutBtnClick = this.handleZoomOutBtnClick.bind(this)
  }

  init(skipFirstTrackSetup) {
    const self = this

    if(!!!skipFirstTrackSetup) {
      const firstTrack = self.tracks[0]

      if(firstTrack) {
        self.setupTrack(firstTrack)
      }
    }
    
    Array.from(self.tracks).forEach((track) => {
      track.removeEventListener("click", self.boundHandleTrackSelection)
      track.addEventListener("click", self.boundHandleTrackSelection)
    })

    if(self.playBtn) {
      self.playBtn.removeEventListener("click", self.boundHandlePlayBtnClick)
      self.prevBtn.removeEventListener("click", self.boundHandlePrevBtnClick)
      self.nextBtn.removeEventListener("click", self.boundHandleNextBtnClick)
  
      self.playBtn.addEventListener("click", self.boundHandlePlayBtnClick)
      self.prevBtn.addEventListener("click", self.boundHandlePrevBtnClick)
      self.nextBtn.addEventListener("click", self.boundHandleNextBtnClick)
    }

    if(self.zoomInBtn) {
      self.zoomInBtn.removeEventListener("click", self.boundHandleZoomInBtnClick)
      self.zoomInBtn.addEventListener("click", self.boundHandleZoomInBtnClick)
    }

    if(self.zoomOutBtn) {
      self.zoomOutBtn.removeEventListener("click", self.boundHandleZoomOutBtnClick)
      self.zoomOutBtn.addEventListener("click", self.boundHandleZoomOutBtnClick)
    }
  }
  reinit() { this.init(true) }

  get activePlayIcon() {
    return this.activeTrack.querySelector(".playlist-track-play i")
  }

  get activeTrack() {
    return this.track(this.activeTrackWaveformDataUrl)
  }

  clearPlayer() {
    this.waveform && this.waveform.destroy()
    this.audioPlayer && this.audioPlayer.destroy()
    if(this.activeTrackWaveformDataUrl) {
      this.activePlayIcon.classList.remove("fa-pause")
      this.activePlayIcon.classList.add("fa-play")
    }
    this.activeTrackWaveformDataUrl = null
    this.activeTrackDuration = null
    Array.from(this.tracks).forEach((track) => track.classList.remove("playing"))
  }

  destroy() {
    this.waveform && this.waveform.destroy()
    this.audioPlayer && this.audioPlayer.destroy()
  }

  handleTrackSelection(e) {
    const currentTarget = e.currentTarget
    const playlistTrackName = currentTarget.querySelector(".playlist-track-name")
    const playlistActions = currentTarget.querySelector(".playlist-actions")

    if((playlistActions == null || !playlistActions.contains(e.target)) && !playlistTrackName.classList.contains("form-shown")) {
      this.playTrack(e.currentTarget)
    }
  }

  playTrack(track) {
    const disablePlayback = track.dataset.disablePlayback === "true"

    if(!disablePlayback) {
      if(this.activeTrackWaveformDataUrl === track.dataset.waveformDataUrl) {
        this.togglePlay()
      } else {
        this.setupTrack(track)
        this.audioPlayer.play()
        this.activeTrackPlayTracked = true
        this._trackPlayedTrack(track)
      }
    }
  }

  handleEnd() {
    const currentIndex = Array.from(this.tracks).findIndex((track) => track.dataset.waveformDataUrl === this.activeTrackWaveformDataUrl)
    if(currentIndex < this.tracks.length - 1) {
      this.playTrack(this.tracks[currentIndex + 1])
    } else {
      this.playTrack(this.tracks[0])
    }
  }

  handleNextBtnClick() {
    if(this.tracks.length) {
      const currentIndex = Array.from(this.tracks).findIndex((track) => track.dataset.waveformDataUrl === this.activeTrackWaveformDataUrl)
      if(currentIndex < this.tracks.length - 1) {
        this.playTrack(this.tracks[currentIndex + 1])
      } else {
        this.playTrack(this.tracks[0])
      }
    }
  }

  handlePause() {
    this.activeTrack.querySelectorAll(".playlist-track-play i").forEach((playIcon) => {
      playIcon.classList.remove("fa-pause")
    })
    this.activePlayIcon.classList.add("fa-play")
    this.playIcon.classList.remove("fa-pause")
    this.playIcon.classList.add("fa-play")

    if(this.waveform) {
      this.waveform.unfollowProgress()
    }
  }

  handlePlay() {
    this.activeTrack.querySelectorAll(".playlist-track-play i").forEach((playIcon) => {
      playIcon.classList.remove("fa-play")
    })
    this.activePlayIcon.classList.add("fa-pause")
    this.playIcon.classList.remove("fa-play")
    this.playIcon.classList.add("fa-pause")

    if(this.waveform) {
      this.waveform.followProgress()
    }
  }

  handlePlayBtnClick() {
    if(this.tracks.length) {
      if(this.activeTrackWaveformDataUrl) {
        this.togglePlay()
      } else {
        this.playTrack(this.playlist.querySelector(".playlist-track:first-child"))
      }
    }
  }

  handlePrevBtnClick() {
    if(this.tracks.length) {
      const currentIndex = Array.from(this.tracks).findIndex((track) => track.dataset.waveformDataUrl === this.activeTrackWaveformDataUrl)
      if(currentIndex > 0) {
        this.playTrack(this.tracks[currentIndex - 1])
      } else {
        this.playTrack(this.tracks[this.tracks.length - 1])
      }
    }
  }

  handleProgress(e) {
    // Howler's duration rounds, so use the one from audiowaveform
    const duration = this.activeTrackDuration

    // For display purposes
    const roundedDuration = Math.round(duration)

    // Adjust Howler's seek by the ratio of the difference between Howler's duration and our own that we're using
    const seek = e.duration == 0 ? e.seek : new Decimal(duration).dividedBy(e.duration).times(e.seek) 

    if(this.waveform) {
      if(duration > 0) {
        this.waveform.updateProgress(seek / duration)
        this.timerInner.innerHTML = `${hhMMSS(seek, roundedDuration)} / ${hhMMSS(roundedDuration)}`
      } else {
        this.waveform.updateProgress(0)
        this.timerInner.innerHTML = `${hhMMSS(0, roundedDuration)} / ${hhMMSS(roundedDuration)}`
      }
    }
  }

  handleZoomInBtnClick() {
    if(this.waveform) {
      this.waveform.zoomIn()
    }
  }

  handleZoomOutBtnClick() {
    if(this.waveform) {
      this.waveform.zoomOut()
    }
  }

  get nextBtn() {
    return this.playlist.querySelector(".playlist-next")
  }

  pause() {
    this.audioPlayer.pause()
  }

  get playBtn() {
    return this.playlist.querySelector(".playlist-play")
  }

  get playIcon() {
    return this.playlist.querySelector(".playlist-play i")
  }

  get playlist() {
    return document.getElementsByClassName("playlist")[0]
  }

  get prevBtn() {
    return this.playlist.querySelector(".playlist-prev")
  }

  recreateWaveform() {
    if(this.waveform) {
      const zoom = this.waveform.zoom
      const followProgress = this.waveform.isFollowingProgress()
      this.waveform.destroy()
      this._createWaveform(followProgress, zoom)
    }

    if(this.audioPlayer) {
      this.handleProgress({
        duration: this.audioPlayer.duration(),
        seek: this.audioPlayer.seekPos()
      })
    }
  }

  seek(e) {
    this.audioPlayer.seek(e.data.posPct, false)
  }

  setupTrack(track) {
    this.clearPlayer()
  
    this.activeTrackPlayTracked = false
    this.activeTrackWaveformDataUrl = track.dataset.waveformDataUrl
    this.activeTrackDuration = parseFloat(track.dataset.duration)
    this.activeTrack.classList.add("playing")

    this.timerInner.innerHTML = `${hhMMSS(0, this.activeTrackDuration)} / ${hhMMSS(this.activeTrackDuration)}`

    this.waveformContainer.classList.add("show")

    this._createWaveform(false, 1.0)
    this.audioPlayer = new AudioPlayer({
      url: track.dataset.url,
      keyboard: true
    })
    this.audioPlayer.on("end", this.handleEnd.bind(this))
    this.audioPlayer.on("play", this.handlePlay.bind(this))
    this.audioPlayer.on("pause", this.handlePause.bind(this))
    this.audioPlayer.on("progress", this.handleProgress.bind(this))
  }

  get timerInner() {
    return this.playlist.querySelector(".playlist-timer-inner")
  }

  togglePlay() {
    if(this.audioPlayer.playing) {
      this.audioPlayer.pause()
    } else {
      if(!this.activeTrackPlayTracked) {
        this.activeTrackPlayTracked = true
        this._trackPlayedTrack(this.activeTrack)
      }
      this.audioPlayer.play()
    }
  }

  _trackEvent(name, properties) {
    if(this.playlist.dataset.enableTracking === "true") {
      const xhr = new XMLHttpRequest()
      xhr.open("POST", `${window.location.href}/events`)
      xhr.setRequestHeader("Content-Type", "application/json")
      xhr.send(JSON.stringify({
        name,
        properties
      }))
    }
  }

  _trackPlayedTrack(track) {
    this._trackEvent("Played Track", {
      file_system_object_id: parseInt(track.dataset.trackId)
    })
  }

  track(waveformDataUrl) {
    return this.playlist.querySelector(`.playlist-track[data-waveform-data-url='${waveformDataUrl}']`)
  }

  get tracks() {
    return this.playlist.getElementsByClassName("playlist-track")
  }

  get waveformContainer() {
    return this.playlist.getElementsByClassName("playlist-waveform-container")[0]
  }

  get zoomInBtn() {
    return this.playlist.querySelector(".playlist-zoom-in")
  }

  get zoomOutBtn() {
    return this.playlist.querySelector(".playlist-zoom-out")
  }

  _createWaveform(followProgress, zoom) {
    this.waveform = new Waveform({
      container: this.waveformContainer,
      containerHeight: 40,
      dataUrl: this.activeTrackWaveformDataUrl,
      disableRangeAdjustments: true,
      duration: this.activeTrackDuration,
      hideTimeline: true,
      progressColor: this.playlist.dataset.progressColor,
      waveformColor: this.playlist.classList.contains("dark") ? "#606368" : null,
      followProgress,
      zoom,
    })
    this.waveform.on("waveform:drawn", this._updateZoomStatus.bind(this))
    this.waveform.on("waveform:click", this.seek.bind(this))
    this.waveform.on("zoom:update", this._updateZoomStatus.bind(this))

    if(this.waveform.dataUrl) {
      this.waveform.draw()
    }
  }

  _updateZoomStatus() {
    if(this.zoomInBtn) {
      if(this.waveform.canZoomIn) {
        this.zoomInBtn.classList.remove("disabled")
      } else {
        this.zoomInBtn.classList.add("disabled")
      }
    }

    if(this.zoomOutBtn) {
      if(this.waveform.canZoomOut) {
        this.zoomOutBtn.classList.remove("disabled")
      } else {
        this.zoomOutBtn.classList.add("disabled")
      }
    }
  }
}

window.addEventListener("DOMContentLoaded", () => {
  if(window.Playlist == null) {
    window.Playlist = new Playlist()
    window.Playlist.init()
  }
})